<template>
  <!-- Table Container Card -->
  <b-card no-body>

    <div class="m-2">
      <b-row>
        <!-- Per Page -->
        <b-col
          cols="12"
          md="6"
          class="d-flex align-items-center justify-content-start mb-1 mb-md-0"
        >
          <TableEntries
            v-model="perPage"
            :options="perPageOptions"
          />
          <b-dropdown
            v-if="getUserPermission('EXPORT_CLOSED_ACCOUNTS_REPORT')"
            id="dropdown-1"
            v-ripple.400="'rgba(255, 255, 255, 0.15)'"
            :text="$t('Export')"
            variant="primary"
          >
            <b-dropdown-item @click="csvExport(csvData)">
              {{ $t('CSV') }}
            </b-dropdown-item>
            <b-dropdown-item @click="excelExport(csvData)">
              {{ $t('Excel') }}
            </b-dropdown-item>
          </b-dropdown>
        </b-col>
        <!-- Export -->
        <b-col
          cols="12"
          md="6"
        >
          <div class="d-flex align-items-center justify-content-lg-end">
            <Button
              :label="$t('Filter')"
              size="md"
              icon="FilterIcon"
              show-icon
              class="mr-1"
              @click="showModal('modal-filter')"
            />
            <Button
              :label="$t('Reset Filter')"
              size="md"
              icon="RotateCwIcon"
              show-icon
              @click="resetFilter"
            />
          </div>
        </b-col>
      </b-row>
    </div>

    <b-table
      ref="glTableRef"
      :items="reportData"
      :busy="isBusy"
      :per-page="pageSize"
      :current-page="currentPage"
      responsive
      :fields="translatedTableHeaders"
      primary-key="id"
      show-empty
      :empty-text="$t('No records available')"
      :sort-by.sync="sortBy"
      :sort-desc.sync="sortDesc"
      class="position-relative"
    >

      <template #table-busy>
        <div class="text-center text-danger my-2">
          <b-spinner
            class="align-middle"
            variant="primary"
          />
        </div>
      </template>

      <template #cell(SN)="data">
        {{ getOverallIndex(data.index) }}
      </template>

      <template #cell(startDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>
      <template #cell(endDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>
      <template #cell(loanClosureDate)="data">
        <p class="mb-0">
          {{ data.value ? formatCustomDate(data.value) : '' }}
        </p>
      </template>

      <!-- Column: account number -->
      <template #cell(accountNumber)="data">
        {{ data.value }}
      </template>

      <!-- Column: Account Name -->
      <template #cell(accountName)="data">
        {{ data.value }}
      </template>

      <template #cell(principalRepaid)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : 0 }}
        </span>
      </template>

      <template #cell(disbursedAmount)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : 0 }}
        </span>
      </template>
      <template #cell(refinancedAmount)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : 0 }}
        </span>
      </template>
      <template #cell(interestRepaid)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : 0 }}
        </span>
      </template>
      <template #cell(feesPaid)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : 0 }}
        </span>
      </template>
      <template #cell(penaltyPaid)="data">
        <span class="text-nowrap">
          {{ data.value ? toBalance(data.value) : 0 }}
        </span>
      </template>
    </b-table>

    <TablePagination
      v-model="currentPage"
      :from="from"
      :to="to"
      :total-rows="totalEntries"
      :per-page="perPage"
    />

    <!-- modal -->
    <b-modal
      id="modal-filter"
      ref="my-modal"
      :ok-title="$t('Apply')"
      :cancel-title="$t('Cancel')"
      cancel-variant="outline-secondary"
      centered
      size="lg"
      no-close-on-backdrop
      :title="$t('Filters')"
      modal-footer
      @ok="handleSubmit"
      @cancel="resetFilters"
    >

      <validation-observer ref="customerAccountHistoryRules">
        <b-form>
          <b-row class="match-height">
            <!-- Loan Account Number -->
            <b-col md="6">
              <InputField
                id="account-number"
                v-model="filter.loanAccountNumber"
                :label="$t('Loan Account Number')"
                :placeholder="$t('Enter Loan Account Number')"
                type="text"
              />
            </b-col>
            <!-- Loan Account Number -->
            <!-- Account Name -->
            <b-col md="6">
              <InputField
                id="account-name"
                v-model="filter.accountName"
                :label="$t('Account Name')"
                :placeholder="$t('Enter Loan Account Name')"
                type="text"
              />
            </b-col>
            <!-- Loan Account Name -->
          </b-row>

          <b-row class="match-height">
            <!-- Product -->
            <b-col md="6">
              <SelectField
                id="product"
                v-model="filter.productCode"
                :reduce="option => option.value"
                :label="$t('Product')"
                :placeholder="$t('Select')"
                :options="productOptions"
                item="label"
              />
            </b-col>
            <!-- Product -->
            <!-- Branch -->
            <b-col md="6">
              <SelectField
                id="branch"
                v-model="filter.branchCode"
                :reduce="option => option.value"
                :label="$t('Branch')"
                :placeholder="$t('Select')"
                :options="branchOptions"
                item="label"
                @input="getBranchValue(filter.branchCode)"
              />
            </b-col>
            <!-- Branch -->
            <!-- Start Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-from"
                v-model="filter.closureDateFrom"
                :min-date="new Date()"
                :label="$t('Financial Date (From)')"
                :error-name="$t('Financial Date From')"
                :config="datePickerConfig"
                :placeholder="$t('Date From')"
              />
            </b-col>
            <!-- Start Date -->
            <!-- End Date -->
            <b-col md="6">
              <DatePickerField
                id="financial-date-to"
                v-model="filter.closureDateTo"
                :min-date="new Date()"
                :label="$t('Financial Date (To)')"
                :error-name="$t('Financial Date To')"
                :config="datePickerConfig"
                :placeholder="$t('Date To')"
              />
            </b-col>
            <!-- End Date -->

            <!-- Account Officer -->
            <b-col md="6">
              <SelectField
                id="account-officer"
                v-model="filter.accountOfficerStaffID"
                :reduce="option => option.value"
                :label="$t('Account Officer')"
                :placeholder="$t('Select')"
                :options="accountOfficerOptions"
                item="label"
              />
            </b-col>
            <!-- Account Officer -->
            <!-- IPPIS/Oracle ID -->
            <b-col md="6">
              <InputField
                id="ippis-oracle-id"
                v-model="filter.employeeID"
                :label="$t('IPPIS/Oracle ID')"
                :placeholder="$t('Enter IPPIS/Oracle ID')"
                type="text"
                for="ippis-oracle-id"
              />
            </b-col>
            <!--IPPIS/Oracle ID -->
          </b-row>
        </b-form>
      </validation-observer>

      <template
        v-if="isReadonly"
        #modal-footer="{ }"
      >
        <div />
      </template>
    </b-modal>
    <!-- modal -->
  </b-card>

</template>

<script>
import {
  BCard, BRow, BCol, BTable, BSpinner, BForm,
  BModal, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import { ValidationObserver } from 'vee-validate'
import { required } from '@validations'
import { sweetAlert } from '@core/mixins/sweetAlert'
import { tableMixins } from '@core/mixins/table'
import { permissionMixin } from '@core/mixins/permission'
import { modalMixins } from '@core/mixins/modal'
import { mapGetters, mapActions } from 'vuex'
import Ripple from 'vue-ripple-directive'
import XLSX from 'xlsx'
import {
  toBalance, toFormatAmount, formatAmount,
} from '@/helpers/formatAmount'
import TablePagination from '@/views/components/table/TablePagination.vue'
import TableEntries from '@/views/components/table/TableEntries.vue'
import InputField from '../../../forms/InputField.vue'
import Button from '../../../components/button/Button.vue'
import SelectField from '../../../forms/SelectField.vue'
import DatePickerField from '../../../forms/DatePickerField.vue'
import toNumber from '../../../../helpers/toNumber'
import { toCustomDateTime, toCustomDate, toDateWithYearFirst } from '../../../../helpers/formatDate'
import { reportDataDetails } from '../../../../helpers/data'
import { reportPermissions } from '../../../../helpers/permissions'

export default {
  components: {
    BCard,
    BRow,
    BCol,
    Button,
    BSpinner,
    BTable,
    TablePagination,
    TableEntries,
    InputField,
    SelectField,
    BModal,
    BDropdown,
    BDropdownItem,
    BForm,
    ValidationObserver,
    DatePickerField,
  },
  directives: {
    Ripple,
  },
  mixins: [modalMixins, tableMixins, sweetAlert, permissionMixin],
  data() {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())
    const firstDay = new Date(now.getFullYear(), now.getMonth(), 1)
    const todayDay = new Date(today)
    return {
      toBalance,
      toFormatAmount,
      formatAmount,
      formatToDateWithYears: toDateWithYearFirst,
      firstDate: firstDay,
      todayDate: todayDay,
      query: '',
      statusToggleLabel: '',
      isReadonly: false,
      buttonLabel: this.$t('Save'),
      isEditable: false,
      isBusy: false,
      required,
      modalTitle: this.$t('Closed Loan Report'),
      sortBy: 'name',
      sortDesc: false,
      tableHeaders: [
        { key: 'SN', label: 'S/N' },
        { key: 'accountNumber', sortable: true, label: 'Account Number' },
        { key: 'accountName', sortable: true, label: 'Account Name' },
        { key: 'linkedAccount', sortable: true, label: 'Linked Account' },
        { key: 'employeeID', sortable: true, label: 'IPPIS/Oracle ID' },
        { key: 'accountOfficer', sortable: true, label: 'Account Officer' },
        { key: 'branch', sortable: true, label: 'Branch' },
        { key: 'product', sortable: true, label: 'Product' },
        { key: 'tenure', sortable: true, label: 'Tenure' },
        { key: 'startDate', sortable: true, label: 'Start Date' },
        { key: 'endDate', sortable: true, label: 'End Date' },
        { key: 'loanClosureDate', sortable: true, label: 'Loan Closure Date' },
        { key: 'interestRate', sortable: true, label: 'Interest Rate' },
        { key: 'disbursedAmount', sortable: true, label: 'Disbursed Amount' },
        { key: 'refinancedAmount', sortable: true, label: 'Refinanced Amount' },
        { key: 'principalRepaid', sortable: true, label: 'Principal Repaid' },
        { key: 'interestRepaid', sortable: true, label: 'Interest Repaid' },
        { key: 'feesPaid', sortable: true, label: 'Fees Paid' },
        { key: 'penaltyPaid', sortable: true, label: 'Penalty Paid' },
        { key: 'economicSector', sortable: true, label: 'Economic Sector' },
        { key: 'lendingModel', sortable: true, label: 'Lending Model' },
      ],
      productOptions: [],
      accountOfficerOptions: [],
      filter: {
        loanAccountNumber: '',
        accountName: '',
        accountOfficerStaffID: '',
        closureDateFrom: null,
        closureDateTo: null,
        productCode: '',
        branchCode: '',
        employeeID: '',
      },
      datePickerConfig: {
        dateFormat: 'Y-m-d',
        maxDate: 'today',
        defaultDate: '',
      },
      reportData: [],
      allSearchedData: [],
      branchRefCode: '',
      selected: '',
      reportInfo: reportDataDetails,
    }
  },
  computed: {
    ...mapGetters({
      branchOptions: 'operations/branchOptions',
      userDetails: 'auth/getCurrentUser',
    }),
    csvData() {
      return this.exportItems.map(item => ({
        ...item,
      }))
    },
    exportItems() {
      return this.allSearchedData.map(item => ({
        'Account Number': item.accountNumber,
        'Account Name': item.accountName,
        'Linked Account': item.linkedAccount,
        'IPPIS/Oracle ID': item.employeeID,
        'Account Officer': item.accountOfficer,
        'Branch Name': item.branch,
        Product: item.product,
        Tenure: item.tenure,
        'Start Date': this.formatCustomDate(item.startDate),
        'End Date': this.formatCustomDate(item.endDate),
        'Loan Closure Date': this.formatCustomDate(item.loanClosureDate),
        'Interest Rate': item.interestRate,
        'Disbursed Amount': item.disbursedAmount > 0 ? formatAmount(item.disbursedAmount) : 0,
        'Refinanced Amount': item.refinancedAmount > 0 ? formatAmount(item.refinancedAmount) : 0,
        'Principal Repaid': item.principalRepaid > 0 ? formatAmount(item.principalRepaid) : 0,
        'Interest Repaid': item.interestRepaid > 0 ? formatAmount(item.interestRepaid) : 0,
        'Fees Paid': item.feesPaid > 0 ? formatAmount(item.feesPaid) : 0,
        'Penalty Paid': item.penaltyPaid > 0 ? formatAmount(item.penaltyPaid) : 0,
        'Economic Sector': item.economicSector,
        'Lending Model': item.lendingModel,
      }))
    },
    translatedTableHeaders() {
      return this.tableHeaders.map(header => ({ ...header, label: header.label ? this.$t(header.label) : header.key }))
    },
  },
  watch: {
    currentPage(newValue) {
      this.currentPage = newValue
      this.fetchClosedLoanReport()
    },
    perPage(newValue) {
      this.perPage = newValue
      this.fetchClosedLoanReport()
    },
  },
  mounted() {
    this.fetchClosedLoanReport()
    this.fetchProduct()
    this.fetchAccountOfficer()
  },
  methods: {
    ...mapActions({
      getClosedLoanReport: 'reports/getClosedLoanReport',
      getBranchByCode: 'operations/getBranchByCode',
      getProductByTypes: 'account/getProductByTypes',
      getAccountOfficer: 'account/getAccountOfficerList',
    }),

    resetFilters() {
      this.filter = { ...null }
    },
    resetFilter() {
      this.filter = { ...null }
      this.fetchClosedLoanReport()
    },
    async fetchProduct() {
      try {
        const response = await this.getProductByTypes({
          type1: 'Loan',
          type2: 'Savings',
          permission: reportPermissions.VIEW_CLOSED_ACCOUNTS,
        })
        const { data } = response
        if (data.data) {
          this.productOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.code }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async fetchAccountOfficer() {
      try {
        const response = await this.getAccountOfficer({
          search: '',
          page: 0,
          limit: 0,
          permission: reportPermissions.VIEW_CLOSED_ACCOUNTS,
        })
        const { status, data } = response
        if (status === 200 && data.data) {
          this.accountOfficerOptions = data.data.filter(dataItem => dataItem.status === 'Active').map(item => ({ label: item.name, value: item.staffID }))
        }
      } catch (error) {
        this.alertError({ text: error })
      }
    },
    async getBranchValue(dataValue) {
      if (dataValue !== null) {
        try {
          const response = await this.getBranchByCode(dataValue)
          const { status, data } = response
          if (status === 200 && data) {
            this.branchRefCode = data
          }
        } catch (error) {
          this.alertError({ text: error })
        } finally {
          this.isBusy = false
        }
      }
    },
    async handleSubmit(bvModalEvt) {
      bvModalEvt.preventDefault()
      this.$refs.customerAccountHistoryRules.validate().then(async success => {
        if (success) {
          this.fetchClosedLoanReport()
        }
      })
    },
    async fetchClosedLoanReport() {
      try {
        this.hideModal('modal-filter')
        this.isBusy = true
        const response = await this.getClosedLoanReport({
          search: this.filter,
          page: this.currentPage,
          limit: this.perPage,
          permission: reportPermissions.VIEW_CLOSED_ACCOUNTS,
        })
        const { data, status } = response
        if (status === 200 && data.data) {
          this.isBusy = false
          this.reportData = data.data
          this.totalCount = data.recordCount
        } else {
          this.reportData = []
          this.totalCount = 0
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async csvExport() {
      try {
        this.isBusy = true
        const response = await this.getClosedLoanReport({
          search: this.filter,
          page: 1,
          limit: 100000,
          permission: reportPermissions.EXPORT_CLOSED_ACCOUNTS_REPORT,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data
          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          let csvContent = 'data:text/csv;charset=utf-8,'
          csvContent += [
            [orgName],
            ['Closed Loan Report'],
            [`${(this.filter.closureDateFrom !== null && this.filter.closureDateTo !== null) ? `Date Range: ${this.formatCustomDate(this.filter.closureDateFrom)} - ${this.formatCustomDate(this.filter.closureDateTo)}` : 'Date Range: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],

            Object.keys(arrData[0]).join(','),
            ...arrData.map(item => Object.values(item).join(',')),
          ]
            .join('\n')
            .replace(/(^\[)|(\]$)/gm, '')

          const dataValue = encodeURI(csvContent)
          const link = document.createElement('a')
          link.setAttribute('href', dataValue)
          link.setAttribute('download', 'Closed Laon Report.csv')
          link.click()
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },
    async excelExport() {
      try {
        this.isBusy = true
        const response = await this.getClosedLoanReport({
          search: this.filter,
          page: 1,
          limit: 100000,
          permission: reportPermissions.EXPORT_CLOSED_ACCOUNTS_REPORT,
        })
        const { status, data } = response
        if (status === 200) {
          this.allSearchedData = data.data

          this.isBusy = false
          this.isDisabled = false
          const arrData = this.exportItems.map(item => ({ ...item }))
          const orgName = this.userDetails.institutionName.toUpperCase()
          const filename = 'Closed Laon Report.xlsx'
          const headerLists = [
            Object.keys(arrData[0]),
          ]

          const Heading = [
            [orgName],
            ['Closed Loan Report'],
            [`${(this.filter.closureDateFrom !== null && this.filter.closureDateTo !== null) ? `Date Range: ${this.formatCustomDate(this.filter.closureDateFrom)} - ${this.formatCustomDate(this.filter.closureDateTo)}` : 'Date Range: All'}`],
            [`${(this.branchRefCode !== '') ? `${this.branchRefCode.name} - ${this.branchRefCode.code}` : 'Branch: All'}`],
            [],
            headerLists[0], // Headers lists
          ]

          const Data = this.allSearchedData.map(item => ({
            'Account Number': item.accountNumber,
            'Account Name': item.accountName,
            'Linked Account': item.linkedAccount,
            'IPPIS/Oracle ID': item.employeeID,
            'Account Officer': item.accountOfficer,
            'Branch Name': item.branch,
            Product: item.product,
            Tenure: item.tenure,
            'Start Date': this.formatCustomDate(item.startDate),
            'End Date': this.formatCustomDate(item.endDate),
            'Loan Closure Date': this.formatCustomDate(item.loanClosureDate),
            'Interest Rate': item.interestRate,
            'Disbursed Amount': item.disbursedAmount > 0 ? toFormatAmount(item.disbursedAmount) : 0,
            'Refinanced Amount': item.disbursedAmount > 0 ? toFormatAmount(item.refinancedAmount) : 0,
            'Principal Repaid': item.principalRepaid > 0 ? toFormatAmount(item.principalRepaid) : 0,
            'Interest Repaid': item.interestRepaid > 0 ? toFormatAmount(item.interestRepaid) : 0,
            'Fees Paid': item.feesPaid > 0 ? toFormatAmount(item.feesPaid) : 0,
            'Penalty Paid': item.penaltyPaid > 0 ? toFormatAmount(item.penaltyPaid) : 0,
            'Economic Sector': item.economicSector,
            'Lending Model': item.lendingModel,
          }))
          const ws = XLSX.utils.aoa_to_sheet(Heading)
          XLSX.utils.sheet_add_json(ws, Data, {
            skipHeader: true,
            origin: -1,
          })
          const wb = XLSX.utils.book_new()
          XLSX.utils.book_append_sheet(wb, ws, 'Closed Loan Report')
          XLSX.writeFile(wb, filename)
        }
      } catch (error) {
        this.handleError(error)
      } finally {
        this.isBusy = false
      }
    },

    formatNumber: toNumber,
    formatDateTime: toCustomDateTime,
    formatCustomDate: toCustomDate,
  },
}
</script>

<style lang="scss" scoped>
    .per-page-selector {
        width: 90px;
    }

    .invoice-filter-select {
        min-width: 190px;

        ::v-deep .vs__selected-options{
            flex-wrap: nowrap;
        }

        ::v-deep .vs__selected {
            width: 100px;
        }
    }
</style>

<style lang="scss">
    @import '@core/scss/vue/libs/vue-select.scss';
</style>
